import { useEffect, useState } from 'react';
import { Alert, Box, Button, CircularProgress, FormControlLabel, Paper, Step, StepLabel, Stepper, Switch, Typography } from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Page from '../../../components/atoms/page';
import {
  askAiVehicleTypeByVin,
  describeVinLookupError,
  lookupVehicleByVin,
  suggestVinRegexImprovement,
  VehicleTypeAIResponse,
  VinLookupMatch,
  VinRegexSuggestion,
} from '../../../api/vehicles';
import { FetchFromBackoffice, SaveToBackoffice } from '../../../utils/requests';
import { createVehicleBrand, validateBrandInput } from './functions';
import { VehicleBrand, VehicleModel, VehicleVariant } from './types';
import CreateWmi from './createWmi';
import CreateModel from './createModel';
import CreateVariant from './createVariant';

type AddVehicleTypeProps = {
  /** Rendered inside another page (the Try VIN sidebar view) instead of as its own route. */
  embedded?: boolean;
};

const AddVehicleType = ({ embedded }: AddVehicleTypeProps) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const vin = (searchParams.get('vin') ?? '').trim().toUpperCase();
  const [brands, setBrands] = useState<VehicleBrand[]>([]);
  const [models, setModels] = useState<VehicleModel[]>([]);
  const [lookupMatch, setLookupMatch] = useState<VinLookupMatch | null>(null);
  const [aiResponse, setAiResponse] = useState<VehicleTypeAIResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [addingBrand, setAddingBrand] = useState(false);
  const [creatingWmi, setCreatingWmi] = useState(false);
  const [creatingModel, setCreatingModel] = useState(false);
  const [selectedModelId, setSelectedModelId] = useState<number | ''>('');
  const [invertVinColors, setInvertVinColors] = useState(true);
  const [variants, setVariants] = useState<VehicleVariant[]>([]);
  const [selectedVariantId, setSelectedVariantId] = useState<number | ''>('');
  const [loadingVariants, setLoadingVariants] = useState(false);
  const [addingVariant, setAddingVariant] = useState(false);
  const [regexSuggestion, setRegexSuggestion] = useState<VinRegexSuggestion | null>(null);
  const [loadingSuggestion, setLoadingSuggestion] = useState(false);
  const [suggestionError, setSuggestionError] = useState<string | null>(null);
  const [savingSuggestion, setSavingSuggestion] = useState(false);
  const [reloadToken, setReloadToken] = useState(0);
  const [suggestionToken, setSuggestionToken] = useState(0);

  useEffect(() => {
    let active = true;

    const loadContext = async () => {
      setLoading(true);
      setError(null);
      try {
        const [brandData, lookup] = await Promise.all([
          FetchFromBackoffice<VehicleBrand[]>('portal/vehicle/brands'),
          vin.length === 17 ? lookupVehicleByVin(vin) : Promise.resolve(null),
        ]);
        if (!active) return;

        const loadedBrands = brandData ?? [];
        setBrands(loadedBrands);
        const match = lookup?.matches?.[0];
        setLookupMatch(match ?? null);
        let aiDetails: VehicleTypeAIResponse | null = null;
        if (vin.length === 17 && !match) {
          aiDetails = await askAiVehicleTypeByVin(vin);
          if (!active) return;
          setAiResponse(aiDetails);
        } else {
          setAiResponse(null);
        }
        const makeName = aiDetails?.make || match?.make || '';
        const matchedBrand = loadedBrands.find((item) => (
          (match?.makeId && item.brandId === Number(match.makeId))
            || (item.name ?? '').trim().toLowerCase() === makeName.trim().toLowerCase()
            || Boolean(lookup?.wmi && (item.patterns ?? []).some((pattern) => pattern.toUpperCase().includes(lookup.wmi.toUpperCase())))
        ));
        const brandId = matchedBrand?.brandId ?? (match?.makeId ? Number(match.makeId) : undefined);
        if (brandId) {
          const modelData = await FetchFromBackoffice<VehicleModel[]>(
            `portal/vehicle/brands/${brandId}/models`
          );
          if (active) {
            const loadedModels = modelData ?? [];
            setModels(loadedModels);
            const matchedModel = loadedModels.find((item) => (
              (match?.modelId && item.modelId === Number(match.modelId))
                || (item.name ?? '').trim().toLowerCase() === (aiDetails?.model || match?.model || '').trim().toLowerCase()
            ));
            if (match && matchedModel) setSelectedModelId(matchedModel.modelId);
          }
        }
      } catch (loadError) {
        console.error('Unable to load vehicle type context', loadError);
        if (active) setError(describeVinLookupError(
          loadError,
          'Unable to load vehicle type context. You can still enter the details manually.'
        ));
      } finally {
        if (active) setLoading(false);
      }
    };

    void loadContext();
    return () => {
      active = false;
    };
  }, [vin, reloadToken]);

  const wmi = vin.slice(0, 3);
  const lookupMake = aiResponse?.make || lookupMatch?.make || '';
  const brand = brands.find((item) => (
    (lookupMatch?.makeId && item.brandId === Number(lookupMatch.makeId))
      || (item.name ?? '').trim().toLowerCase() === lookupMake.trim().toLowerCase()
      || Boolean(wmi && (item.patterns ?? []).some((pattern) => pattern.toUpperCase().includes(wmi)))
  ));
  const makeName = lookupMake || brand?.name || '';
  const hasWmi = Boolean(brand && wmi && (brand.patterns ?? []).some((pattern) => pattern.toUpperCase() === wmi));
  const makeVerified = Boolean(brand && hasWmi);
  const modelName = aiResponse?.model || lookupMatch?.model || '';
  const normalizedModelName = modelName.trim().toLowerCase();
  const modelCandidates = makeVerified && normalizedModelName
    ? models.filter((item) => {
      const normalizedName = (item.name ?? '').trim().toLowerCase();
      return normalizedName === normalizedModelName
        || normalizedName.includes(normalizedModelName)
        || normalizedModelName.includes(normalizedName);
    })
    : [];
  const similarModelIds = new Set(modelCandidates.map((item) => item.modelId));
  const selectedModel = models.find((item) => item.modelId === selectedModelId);
  const modelVerified = Boolean(makeVerified && selectedModel);

  useEffect(() => {
    if (!lookupMatch) setSelectedModelId('');
  }, [lookupMatch, makeVerified, modelName]);

  useEffect(() => {
    setSelectedVariantId('');
    setVariants([]);
    if (!modelVerified || !selectedModelId) {
      setLoadingVariants(false);
      return;
    }

    let active = true;
    setLoadingVariants(true);
    FetchFromBackoffice<VehicleVariant[]>(
      `portal/vehicle/brands/${brand?.brandId}/models/${selectedModelId}/variants`
    )
      .then((data) => {
        if (active) {
          const loadedVariants = data ?? [];
          setVariants(loadedVariants);
          const matchedVariant = loadedVariants.find((item) => (
            (lookupMatch?.variantId && item.variantId === Number(lookupMatch.variantId))
              || item.name.trim().toLowerCase() === (lookupMatch?.variant || '').trim().toLowerCase()
          ));
          if (lookupMatch && matchedVariant) setSelectedVariantId(matchedVariant.variantId);
        }
      })
      .catch((loadError) => {
        console.error('Unable to load vehicle variants', loadError);
        if (active) setError('Unable to load variants for the selected model.');
      })
      .finally(() => {
        if (active) setLoadingVariants(false);
      });

    return () => {
      active = false;
    };
  }, [brand?.brandId, lookupMatch, modelVerified, selectedModelId]);

  const dimensions = aiResponse && {
    weight: aiResponse.tareKg,
    length: aiResponse.lengthMm,
    height: aiResponse.heightMm,
    width: aiResponse.widthMm,
  };
  const suggestedVariant = {
    name: aiResponse?.variantName || lookupMatch?.variant || '',
    powertrain: /ev|electric|bev/i.test(aiResponse?.powerTrain ?? '') ? 'EV' : 'ICE',
    fromYear: aiResponse?.fromYear ?? lookupMatch?.fromYear ?? '',
    toYear: aiResponse?.toYear ?? lookupMatch?.toYear ?? null,
    weight: aiResponse?.tareKg ?? 0,
    length: aiResponse?.lengthMm ?? 0,
    width: aiResponse?.widthMm ?? 0,
    height: aiResponse?.heightMm ?? 0,
    // A pattern built from the WMI, VDS and check digit; the modal can ask for a better one.
    regexPattern: vin.length === 17 ? `^${vin.slice(0, 8)}[A-HJ-NPR-Z0-9]{9}$` : '',
  };
  const variantDistances = variants.map((variant) => {
    if (!dimensions) return { variant, distance: null };
    const values = [
      [variant.weight, dimensions.weight],
      [variant.length, dimensions.length],
      [variant.height, dimensions.height],
      [variant.width, dimensions.width],
    ];
    const distance = values.reduce((total, [actual, expected]) => (
      total + Math.abs(actual - expected) / Math.max(expected, 1)
    ), 0);
    return { variant, distance };
  });
  const closestDistance = variantDistances.reduce<number | null>(
    (closest, item) => item.distance !== null && (closest === null || item.distance < closest) ? item.distance : closest,
    null
  );
  const selectedVariant = variants.find((item) => item.variantId === selectedVariantId);
  const variantVerified = Boolean(selectedVariant);
  let vinMatchesVariant = false;
  let regexError = false;
  if (selectedVariant?.regexPattern && vin) {
    try {
      vinMatchesVariant = new RegExp(selectedVariant.regexPattern, 'i').test(vin);
    } catch {
      regexError = true;
    }
  }
  const matchedLookupRegex = lookupMatch?.regEx && vin
    ? (() => {
      try {
        return new RegExp(lookupMatch.regEx, 'i').test(vin);
      } catch {
        return false;
      }
    })()
    : false;
  const regexVerified = Boolean(
    selectedVariant && !regexError && (vinMatchesVariant || matchedLookupRegex)
  );

  const variantPattern = selectedVariant?.regexPattern?.trim() ?? '';
  const suggestionBrand = brand?.name || makeName;
  const suggestionModel = selectedModel?.name || modelName;
  const needsSuggestion = Boolean(
    vin && selectedVariant && variantPattern && (regexError || !vinMatchesVariant)
  );

  useEffect(() => {
    setRegexSuggestion(null);
    setSuggestionError(null);
    if (!needsSuggestion) {
      setLoadingSuggestion(false);
      return;
    }

    let active = true;
    setLoadingSuggestion(true);
    suggestVinRegexImprovement(vin, variantPattern, suggestionBrand, suggestionModel)
      .then((data) => {
        if (active) setRegexSuggestion(data ?? null);
      })
      .catch((suggestError) => {
        console.error('Unable to fetch a suggested VIN pattern', suggestError);
        if (active) setSuggestionError(describeVinLookupError(
          suggestError,
          'Unable to fetch a suggested VIN pattern.'
        ));
      })
      .finally(() => {
        if (active) setLoadingSuggestion(false);
      });

    return () => {
      active = false;
    };
  }, [needsSuggestion, vin, variantPattern, suggestionBrand, suggestionModel, suggestionToken]);

  const suggestedPattern = regexSuggestion?.result?.regexPattern?.trim() ?? '';
  let suggestedMatchesVin = false;
  let suggestedPatternInvalid = false;
  if (suggestedPattern && vin) {
    try {
      suggestedMatchesVin = new RegExp(suggestedPattern, 'i').test(vin);
    } catch {
      suggestedPatternInvalid = true;
    }
  }

  const handleAddBrand = async () => {
    const validationError = validateBrandInput(makeName, wmi);
    if (validationError) {
      setError(validationError);
      return;
    }

    setAddingBrand(true);
    setError(null);
    try {
      const createdBrand = await createVehicleBrand(makeName, wmi);
      setBrands((current) => [...current, createdBrand]);
    } catch (createError) {
      console.error('Unable to create vehicle brand', createError);
      setError(`Unable to add ${makeName} as a brand.`);
    } finally {
      setAddingBrand(false);
    }
  };

  const applySuggestedPattern = async () => {
    if (!brand || !selectedVariant || !suggestedPattern) return;

    setSavingSuggestion(true);
    setSuggestionError(null);
    try {
      await SaveToBackoffice(
        `portal/vehicle/brands/${brand.brandId}/models/${selectedVariant.modelId}/variants/${selectedVariant.variantId}`,
        {
          variantId: selectedVariant.variantId,
          brandId: brand.brandId,
          modelId: selectedVariant.modelId,
          name: selectedVariant.name,
          powertrain: selectedVariant.powertrain,
          fromYear: selectedVariant.fromYear,
          toYear: selectedVariant.toYear,
          externalId: selectedVariant.externalId,
          weight: selectedVariant.weight,
          length: selectedVariant.length,
          height: selectedVariant.height,
          width: selectedVariant.width,
          regexPattern: suggestedPattern,
        }
      );
      setVariants((current) => current.map((item) => (
        item.variantId === selectedVariant.variantId
          ? { ...item, regexPattern: suggestedPattern }
          : item
      )));
    } catch (saveError) {
      console.error('Unable to save the suggested VIN pattern', saveError);
      setSuggestionError('Unable to save the suggested VIN pattern.');
    } finally {
      setSavingSuggestion(false);
    }
  };

  return (
    <Page title="Add vehicle type" linkBack={embedded ? undefined : navigate}>
      {loading && <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress /></Box>}
      {error && <Alert
        severity="warning"
        sx={{ mb: 2 }}
        action={<Button color="inherit" size="small" disabled={loading} onClick={() => setReloadToken((token) => token + 1)}>Try again</Button>}
      >
        {error}
      </Alert>}
      <Stepper activeStep={addingVariant ? 4 : regexVerified ? 4 : variantVerified ? 3 : modelVerified ? 2 : makeVerified ? 1 : 0} sx={{ mb: 3 }}>
        <Step><StepLabel>Verify make</StepLabel></Step>
        <Step><StepLabel>Verify model</StepLabel></Step>
        <Step><StepLabel>Verify variant</StepLabel></Step>
        <Step><StepLabel>Validate regex</StepLabel></Step>
        <Step><StepLabel>Add vehicle type</StepLabel></Step>
      </Stepper>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>Step 1: Verify make</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Confirm that the VIN make and its WMI are configured in the system before adding a vehicle type.
        </Typography>
        <Box sx={{ mb: 3, px: { xs: 1, sm: 2 }, py: { xs: 1, sm: 2 } }}>
          <Typography
            variant="h6"
            sx={{
              display: 'inline-block',
              px: 2,
              py: 1,
              border: '2px solid',
              borderColor: 'common.black',
              backgroundColor: invertVinColors ? 'common.black' : 'common.white',
              color: invertVinColors ? 'common.white' : 'common.black',
              fontFamily: '"Courier New", Courier, monospace',
              fontSize: { xs: '1.25rem', sm: '1.5rem' },
              fontWeight: 700,
              letterSpacing: 2,
              lineHeight: 1.4,
              whiteSpace: 'nowrap',
              userSelect: 'text',
            }}
          >
            {vin || 'Not provided'}
          </Typography>
          <FormControlLabel
            control={<Switch checked={invertVinColors} onChange={(event) => setInvertVinColors(event.target.checked)} />}
            label="Invert colors"
            sx={{ display: 'block', mt: 1 }}
          />
        </Box>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 2, alignItems: 'center' }}>
          <Box><Typography variant="caption" color="text.secondary">Make</Typography><Typography>{makeName || 'Not found'}</Typography></Box>
          <Box><Typography variant="caption" color="text.secondary">VIN WMI</Typography><Typography>{wmi || 'Not available'}</Typography></Box>
          <Box><Typography variant="caption" color="text.secondary">System status</Typography><Typography color={makeVerified ? 'success.main' : 'error.main'}>{makeVerified ? 'Verified' : 'Action required'}</Typography></Box>
        </Box>
        {!brand && makeName && (
          <Button variant="contained" color="error" sx={{ mt: 3 }} disabled={addingBrand} onClick={handleAddBrand}>
            {addingBrand ? 'Adding…' : `Add ${makeName} with WMI ${wmi}`}
          </Button>
        )}
        {brand && !hasWmi && (
          <Button variant="contained" color="error" sx={{ mt: 3 }} onClick={() => setCreatingWmi(true)}>
            Add WMI to {brand.name}
          </Button>
        )}
        {!makeName && <Alert severity="info" sx={{ mt: 3 }}>No make was found for this VIN. Use the VIN lookup before continuing.</Alert>}
      </Paper>

      {makeVerified && <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>Step 2: Verify model</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Confirm that the VIN model is configured for the verified make before adding a vehicle type.
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 2 }}>
          <Box><Typography variant="caption" color="text.secondary">Make</Typography><Typography>{brand?.name}</Typography></Box>
          <Box><Typography variant="caption" color="text.secondary">VIN model</Typography><Typography>{modelName || 'Not found'}</Typography></Box>
        </Box>
        {models.length > 0 && (
          <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle2" gutterBottom>
              Select an existing model{modelName ? ` (similar to "${modelName}" are highlighted)` : ''}:
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {models.map((candidate) => {
                const isSimilar = similarModelIds.has(candidate.modelId);
                return (
                <Button
                  key={candidate.modelId}
                  variant={selectedModelId === candidate.modelId || isSimilar ? 'contained' : 'outlined'}
                  color={selectedModelId === candidate.modelId ? 'primary' : isSimilar ? 'success' : 'inherit'}
                  sx={isSimilar && selectedModelId !== candidate.modelId ? { fontWeight: 'bold' } : undefined}
                  onClick={() => setSelectedModelId(candidate.modelId)}
                >
                  {candidate.name}
                </Button>
                );
              })}
            </Box>
          </Box>
        )}
        {makeVerified && (
          <Button variant="outlined" color="error" sx={{ mt: 3 }} onClick={() => setCreatingModel(true)}>
            Add new model
          </Button>
        )}
        {modelVerified && <Typography color="success.main" sx={{ mt: 3 }}>Model verified: {selectedModel?.name}</Typography>}
      </Paper>}

      {modelVerified && <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>Step 3: Verify variant</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Select any existing variant for this model. Variants closest to the AI dimensions are highlighted.
        </Typography>
        {dimensions && <Paper variant="outlined" sx={{ p: 2, mb: 3, backgroundColor: 'action.hover' }}>
          <Typography variant="subtitle2" gutterBottom>Dimensions from AI</Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 2 }}>
            <Box><Typography variant="caption" color="text.secondary">Weight</Typography><Typography>{dimensions.weight} kg</Typography></Box>
            <Box><Typography variant="caption" color="text.secondary">Length</Typography><Typography>{dimensions.length} mm</Typography></Box>
            <Box><Typography variant="caption" color="text.secondary">Width</Typography><Typography>{dimensions.width} mm</Typography></Box>
            <Box><Typography variant="caption" color="text.secondary">Height</Typography><Typography>{dimensions.height} mm</Typography></Box>
          </Box>
        </Paper>}
        {loadingVariants && <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}><CircularProgress /></Box>}
        {!loadingVariants && variants.length === 0 && <Alert severity="info">No variants exist for this model yet.</Alert>}
        {variantDistances.length > 0 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {variantDistances.map(({ variant, distance }) => {
              const isClosest = distance !== null && distance === closestDistance;
              return (
                <Button
                  key={variant.variantId}
                  variant={selectedVariantId === variant.variantId ? 'contained' : 'outlined'}
                  color={selectedVariantId === variant.variantId ? 'primary' : isClosest ? 'success' : 'inherit'}
                  sx={{ justifyContent: 'space-between', textTransform: 'none', fontWeight: isClosest ? 'bold' : 'normal' }}
                  onClick={() => setSelectedVariantId(variant.variantId)}
                >
                  <span>{variant.name}</span>
                  <span>
                    {isClosest ? 'Closest dimensions | ' : ''}
                    {variant.weight} kg | {variant.length} x {variant.width} x {variant.height} mm
                  </span>
                </Button>
              );
            })}
          </Box>
        )}
        <Button variant="outlined" color="error" sx={{ mt: 3 }} onClick={() => setAddingVariant(true)}>
          Add new variant
        </Button>
        {variantVerified && <Typography color="success.main" sx={{ mt: 3 }}>Variant selected: {selectedVariant?.name}</Typography>}
      </Paper>}

      {variantVerified && <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>Step 4: Validate regex</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Check whether the VIN matches the selected variant&apos;s existing pattern.
        </Typography>
        <Typography variant="subtitle2" gutterBottom>Existing VIN pattern</Typography>
        <Paper variant="outlined" sx={{ p: 2, mb: 2, fontFamily: 'monospace', overflowX: 'auto' }}>
          {selectedVariant?.regexPattern || 'No pattern configured'}
        </Paper>
        {regexError && <Alert severity="error">The existing VIN pattern is not a valid regular expression.</Alert>}
        {!regexError && selectedVariant?.regexPattern && (
          <Alert severity={vinMatchesVariant ? 'success' : 'error'}>
            VIN {vinMatchesVariant ? 'matches' : 'does not match'} this variant pattern.
          </Alert>
        )}
        {!selectedVariant?.regexPattern && <Alert severity="warning">No VIN pattern is configured for this variant.</Alert>}

        {needsSuggestion && <Box sx={{ mt: 3 }}>
          <Typography variant="subtitle2" gutterBottom>Suggested VIN pattern</Typography>
          {loadingSuggestion && <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 1 }}>
            <CircularProgress size={20} />
            <Typography variant="body2" color="text.secondary">Asking for a suggested pattern…</Typography>
          </Box>}
          {suggestionError && <Alert
            severity="warning"
            sx={{ mb: 2 }}
            action={<Button color="inherit" size="small" disabled={loadingSuggestion} onClick={() => setSuggestionToken((token) => token + 1)}>Try again</Button>}
          >
            {suggestionError}
          </Alert>}
          {!loadingSuggestion && suggestedPattern && (
            <>
              <Paper variant="outlined" sx={{ p: 2, mb: 2, fontFamily: 'monospace', overflowX: 'auto' }}>
                {suggestedPattern}
              </Paper>
              {suggestedPatternInvalid && <Alert severity="error" sx={{ mb: 2 }}>The suggested pattern is not a valid regular expression.</Alert>}
              {!suggestedPatternInvalid && (
                <Alert severity={suggestedMatchesVin ? 'success' : 'warning'} sx={{ mb: 2 }}>
                  VIN {suggestedMatchesVin ? 'matches' : 'still does not match'} the suggested pattern.
                </Alert>
              )}
              {regexSuggestion?.result?.description && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {regexSuggestion.result.description}
                </Typography>
              )}
              <Button
                variant="contained"
                disabled={savingSuggestion || !suggestedMatchesVin}
                onClick={applySuggestedPattern}
              >
                {savingSuggestion ? 'Saving…' : 'Save suggested pattern to variant'}
              </Button>
            </>
          )}
        </Box>}
      </Paper>}

      <CreateVariant
        open={addingVariant}
        onClose={() => setAddingVariant(false)}
        brandId={brand?.brandId ?? ''}
        modelId={selectedModel?.modelId ?? ''}
        brandName={brand?.name ?? makeName}
        brandPatterns={brand?.patterns ?? []}
        modelName={selectedModel?.name ?? modelName}
        vin={vin}
        initialValues={suggestedVariant}
        onCreated={(createdVariant) => {
          setVariants((current) => [...current, createdVariant]);
          setSelectedVariantId(createdVariant.variantId);
          setAddingVariant(false);
        }}
      />

      <CreateWmi
        open={creatingWmi}
        onClose={() => setCreatingWmi(false)}
        brandId={brand?.brandId ?? ''}
        brandName={brand?.name ?? makeName}
        initialCode={wmi}
        onCreated={(code) => {
          if (!brand) return;
          setBrands((current) => current.map((item) => (
            item.brandId === brand.brandId
              ? { ...item, patterns: [...(item.patterns ?? []), code] }
              : item
          )));
          setCreatingWmi(false);
        }}
      />
      <CreateModel
        open={creatingModel}
        onClose={() => setCreatingModel(false)}
        brandId={brand?.brandId ?? ''}
        brandName={brand?.name ?? ''}
        initialName={modelName}
        onCreated={(createdModel) => {
          setModels((current) => [...current, createdModel]);
          setSelectedModelId(createdModel.modelId);
          setCreatingModel(false);
        }}
      />
    </Page>
  );
};

export default AddVehicleType;
